import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { MaterialDataTable } from '../../table-common/MaterialDataTable';
import {
  EVENT_SORT_KEYS,
  getEnvironmentColumns,
  toChildRow,
  formatCarCount,
} from './event-history-data';

const PARENT_ID_PREFIX = 'consist::';
const EMPTY_LIST = [];
const EMPTY_MAP = {};

// Timestamp/EventId/Name/Action re-fetch from backend; Consist/Car reorder the already-loaded parent groups.
const SortableHeader = ({ label, sortKey, consistSortType, consistReversed, onSortConsist }) => (
  <button
    type="button"
    className="consist-event-history__sort-header"
    onClick={() => onSortConsist?.(sortKey)}
  >
    {label}
    {consistSortType === sortKey && (
      <i className={`fa ${consistReversed[sortKey] ? 'fa-caret-down' : 'fa-caret-up'}`} />
    )}
  </button>
);

// Sizes mirror the Unit table column width percentages (5/5/11/5/14/5).
const getBaseColumns = ({
  consistEventsLoading,
  expandedConsists,
  onToggleConsist,
  consistSortType,
  consistReversed,
  onSortConsist,
  parentSortKey,
  parentReversed,
  onSortParent,
}) => [
  {
    accessorKey: 'consist',
    header: 'Consist',
    size: 50,
    Header: () => (
      <SortableHeader
        label="Consist"
        sortKey="CONSIST"
        consistSortType={parentSortKey}
        consistReversed={parentReversed}
        onSortConsist={onSortParent}
      />
    ),
    Cell: ({ row, cell }) => {
      if (row.depth > 0) return cell.getValue();

      const isLoading = !!consistEventsLoading[row.original.consist];
      const isExpanded = !!expandedConsists[row.id];
      const chevronClass = isExpanded
        ? 'fa fa-chevron-right consist-event-history__chevron is-expanded'
        : 'fa fa-chevron-right consist-event-history__chevron';

      return (
        <button
          type="button"
          className="consist-event-history__toggle"
          data-testid={`consist-event-history-toggle-button-${row.original.consist}`}
          onClick={event => {
            // Row handles the click too; stop here so it toggles only once.
            event.stopPropagation();
            onToggleConsist(row);
          }}
          aria-expanded={isExpanded}
        >
          <i className={isLoading ? 'fas fa-circle-notch fa-spin consist-event-history__chevron' : chevronClass} />
          {cell.getValue()}
        </button>
      );
    },
  },
  {
    accessorKey: 'car',
    header: 'Car',
    size: 50,
    Header: () => (
      <SortableHeader
        label="Car"
        sortKey="CAR"
        consistSortType={parentSortKey}
        consistReversed={parentReversed}
        onSortConsist={onSortParent}
      />
    ),
  },
  {
    accessorKey: 'timestamp',
    header: 'Timestamp',
    size: 110,
    Header: () => (
      <SortableHeader
        label="Timestamp"
        sortKey={EVENT_SORT_KEYS.ACTIVATED}
        consistSortType={consistSortType}
        consistReversed={consistReversed}
        onSortConsist={onSortConsist}
      />
    ),
  },
  {
    accessorKey: 'eventId',
    header: 'Event ID',
    size: 50,
    Header: () => (
      <SortableHeader
        label="Event ID"
        sortKey={EVENT_SORT_KEYS.EVENT_ID}
        consistSortType={consistSortType}
        consistReversed={consistReversed}
        onSortConsist={onSortConsist}
      />
    ),
  },
  {
    accessorKey: 'name',
    header: 'Name',
    size: 140,
    Header: () => (
      <SortableHeader
        label="Name"
        sortKey={EVENT_SORT_KEYS.TITLE}
        consistSortType={consistSortType}
        consistReversed={consistReversed}
        onSortConsist={onSortConsist}
      />
    ),
  },
  {
    accessorKey: 'action',
    header: 'Action',
    size: 50,
    Header: () => (
      <SortableHeader
        label="Action"
        sortKey={EVENT_SORT_KEYS.ACTION}
        consistSortType={consistSortType}
        consistReversed={consistReversed}
        onSortConsist={onSortConsist}
      />
    ),
  },
];

// Renders when a consist was fetched (subsystems/events filters already applied server-side) with zero matches.
const buildEmptyStateRow = environmentColumns => ({
  consist: '',
  car: '',
  timestamp: '',
  eventId: '',
  name: 'No events match the current filters for this consist.',
  action: '',
  isEmptyState: true,
  ...environmentColumns.reduce((values, column) => ({ ...values, [column.accessorKey]: '' }), {}),
});

// Parent rows come from the fleet consist list so they are not limited to the events paged in so far.
const toConsistRows = (events, environmentColumns, consistList, consistEvents, formatTimestamp) => {
  const eventsByConsist = new Map();
  events.forEach(event => {
    const consist = event.consist || '';
    if (!eventsByConsist.has(consist)) eventsByConsist.set(consist, []);
    eventsByConsist.get(consist).push(toChildRow(event, environmentColumns, formatTimestamp));
  });

  const rows = consistList.map(({ trainId, cars = [] }) => {
    const fetchedEvents = consistEvents[trainId];
    let subRows;
    if (fetchedEvents) {
      subRows = fetchedEvents.length
        ? fetchedEvents.map(event => toChildRow(event, environmentColumns, formatTimestamp))
        : [buildEmptyStateRow(environmentColumns)];
    } else {
      subRows = eventsByConsist.get(trainId) || [];
    }

    return {
      consist: trainId,
      car: formatCarCount(cars.length),
      carCount: cars.length,
      subRows,
    };
  });

  const knownConsists = new Set(consistList.map(({ trainId }) => trainId));
  eventsByConsist.forEach((subRows, consist) => {
    if (!knownConsists.has(consist)) {
      const carCount = new Set(subRows.map(row => row.car)).size;
      rows.push({ consist, car: formatCarCount(carCount), carCount, subRows });
    }
  });

  return rows;
};

// Consist/Car reorder already-loaded parent rows client-side; no backend call needed.
const sortParentRows = (rows, sortKey, reversed) => {
  if (!sortKey) return rows;

  const sorted = [...rows].sort((a, b) =>
    sortKey === 'CAR'
      ? a.carCount - b.carCount
      : String(a.consist).localeCompare(String(b.consist), undefined, { numeric: true, sensitivity: 'base' })
  );

  return reversed ? sorted.reverse() : sorted;
};

export const ConsistEventHistoryTable = ({
  eventHistory = EMPTY_LIST,
  consistList = EMPTY_LIST,
  consistEvents = EMPTY_MAP,
  consistEventsLoading = EMPTY_MAP,
  selectedEnvData = EMPTY_LIST,
  loadBusy = false,
  hasMore = false,
  isActive = false,
  consistSortType = 'ACTIVATED',
  consistReversed = EMPTY_MAP,
  onLoadMore,
  onExpandConsist,
  onSortConsist,
  formatTimestampFilter,
}) => {
  // size is a presentation concern, kept out of the shared column definitions.
  const environmentColumns = useMemo(
    () => getEnvironmentColumns(selectedEnvData).map(column => ({ ...column, size: 50 })),
    [selectedEnvData]
  );
  const [expandedConsists, setExpandedConsists] = useState({});
  const [parentSortKey, setParentSortKey] = useState(null);
  const [parentReversed, setParentReversed] = useState({ CONSIST: true, CAR: true });
  // Only one sort family (Consist/Car vs. Timestamp/Event ID/Name/Action) can be active at a time.
  const [activeSortFamily, setActiveSortFamily] = useState(null);

  const handleToggleConsist = useCallback(row => {
    const consistId = row.original.consist;
    const isExpanded = !!expandedConsists[row.id];
    if (!isExpanded && !consistEvents[consistId]) {
      onExpandConsist?.(consistId);
    }
    setExpandedConsists(prev => ({ ...prev, [row.id]: !isExpanded }));
  }, [expandedConsists, consistEvents, onExpandConsist]);

  const handleSortParent = useCallback(sortKey => {
    setActiveSortFamily('parent');
    setParentSortKey(sortKey);
    setParentReversed(prev => ({ ...prev, [sortKey]: !prev[sortKey] }));
  }, []);

  // A filter change clears the whole consist cache (see loadEventHistoryData); refetch any
  // row that's still expanded but lost its cached events, instead of showing it blank.
  useEffect(() => {
    Object.keys(expandedConsists)
      .filter(id => expandedConsists[id] && id.startsWith(PARENT_ID_PREFIX))
      .map(id => id.slice(PARENT_ID_PREFIX.length))
      .forEach(consistId => {
        if (!consistEvents[consistId] && !consistEventsLoading[consistId]) {
          onExpandConsist?.(consistId);
        }
      });
  }, [consistEvents, expandedConsists, consistEventsLoading, onExpandConsist]);

  // Re-fetches the sort order for any consists already expanded; collapsed ones refetch on next expand.
  const handleSortConsist = useCallback(sortKey => {
    setActiveSortFamily('column');
    const expandedConsistIds = Object.keys(expandedConsists)
      .filter(id => expandedConsists[id] && id.startsWith(PARENT_ID_PREFIX))
      .map(id => id.slice(PARENT_ID_PREFIX.length));
    onSortConsist?.(sortKey, expandedConsistIds);
  }, [expandedConsists, onSortConsist]);

  const columns = useMemo(() => [
    ...getBaseColumns({
      consistEventsLoading,
      expandedConsists,
      onToggleConsist: handleToggleConsist,
      consistSortType: activeSortFamily === 'column' ? consistSortType : null,
      consistReversed,
      onSortConsist: handleSortConsist,
      parentSortKey: activeSortFamily === 'parent' ? parentSortKey : null,
      parentReversed,
      onSortParent: handleSortParent,
    }),
    ...environmentColumns,
  ], [consistEventsLoading, expandedConsists, handleToggleConsist, activeSortFamily,
    consistSortType, consistReversed, handleSortConsist, parentSortKey, parentReversed,
    handleSortParent, environmentColumns]);

  const consistRows = useMemo(
    () => toConsistRows(eventHistory, environmentColumns, consistList, consistEvents, formatTimestampFilter),
    [eventHistory, environmentColumns, consistList, consistEvents, formatTimestampFilter]
  );
  const rows = useMemo(() => sortParentRows(
    consistRows,
    activeSortFamily === 'parent' ? parentSortKey : null,
    parentReversed[parentSortKey]
  ), [consistRows, activeSortFamily, parentSortKey, parentReversed]);

  return (
    <MaterialDataTable
      className="event-history-container consist-event-history"
      tableTestId="consist-event-history-table"
      scrollContainerClassName="consist-event-history__scroll"
      fitViewport
      fillViewport
      ariaLabel="History event list"
      enableTopToolbar
      enableFullScreenToggle
      fullScreenToggleTestId="event-history-fullscreen-toggle-button"
      columns={columns}
      data={rows}
      isLoading={loadBusy}
      enableExpanding
      getRowCanExpand={row => row.depth === 0}
      getSubRows={row => row.subRows}
      getRowId={(row, index, parent) => (parent ? `${parent.id}::${index}` : `${PARENT_ID_PREFIX}${row.consist}`)}
      expandedRows={expandedConsists}
      onExpandedChange={setExpandedConsists}
      hideExpandColumn
      onReachBottom={() => onLoadMore?.({ reload: false })}
      canLoadMore={isActive && hasMore}
      muiTableBodyRowProps={({ row }) => {
        if (row.depth === 0) {
          return {
            className: 'consist-parent-row',
            onClick: () => handleToggleConsist(row),
            'data-testid': `consist-event-history-row-${row.original.consist}`,
          };
        }
        return {
          className: row.original.isEmptyState ? 'consist-child-row consist-empty-row' : 'consist-child-row',
          'data-testid': `consist-event-history-row-${row.id}`,
        };
      }}
      muiTableBodyCellProps={({ column, row }) => ({
        'data-testid': `consist-event-history-cell-${row.id}-${column.id}`,
      })}
    />
  );
};

ConsistEventHistoryTable.propTypes = {
  formatTimestampFilter: PropTypes.func.isRequired,
  eventHistory: PropTypes.array,
  consistList: PropTypes.arrayOf(PropTypes.shape({
    trainId: PropTypes.string,
    cars: PropTypes.array,
  })),
  consistEvents: PropTypes.object,
  consistEventsLoading: PropTypes.object,
  selectedEnvData: PropTypes.array,
  loadBusy: PropTypes.bool,
  hasMore: PropTypes.bool,
  isActive: PropTypes.bool,
  consistSortType: PropTypes.string,
  consistReversed: PropTypes.object,
  onLoadMore: PropTypes.func,
  onExpandConsist: PropTypes.func,
  onSortConsist: PropTypes.func,
};